import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Stack
} from '@mui/material';
import { Stars, CheckCircle, Close } from '@mui/icons-material';
import PaymentsService, { Package } from '../services/payments';

interface PricingModalProps {
  open: boolean;
  onClose: () => void;
}

const PricingModal: React.FC<PricingModalProps> = ({
  open,
  onClose,
}) => {
  const [packages, setPackages] = useState<Package[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [purchasing, setPurchasing] = useState<string | null>(null);
  const [currentCredits, setCurrentCredits] = useState(0);

  useEffect(() => {
    if (open) {
      fetchPackages();
      fetchCredits();
    }
  }, [open]);

  const fetchPackages = async () => {
    try {
      setLoading(true);
      const data = await PaymentsService.getPackages();
      setPackages(data);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch packages:', err);
      setError('Failed to load pricing packages');
    } finally {
      setLoading(false);
    }
  };

  const fetchCredits = async () => {
    try {
      const data = await PaymentsService.getCredits();
      setCurrentCredits(data.credits);
    } catch (err) {
      console.error('Failed to fetch credits:', err);
    }
  };

  const handlePurchase = async (packageId: string) => {
    try {
      setPurchasing(packageId);
      await PaymentsService.purchasePackage(packageId);
      // User will be redirected to Stripe checkout
    } catch (err) {
      console.error('Failed to start checkout:', err);
      setError('Failed to start checkout. Please try again.');
      setPurchasing(null);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3 }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h5" fontWeight={700}>
            Purchase Credits
          </Typography>
          <Button
            onClick={onClose}
            sx={{ minWidth: 'auto', p: 1 }}
            disabled={purchasing !== null}
          >
            <Close />
          </Button>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Choose a package to continue mastering your tracks
        </Typography>
      </DialogTitle>

      <DialogContent>
        {/* Current Balance */}
        <Alert
          severity="info"
          icon={<Stars />}
          sx={{ mb: 3, borderRadius: 2 }}
        >
          <Typography variant="body2">
            <strong>Current Balance:</strong> {currentCredits} {currentCredits === 1 ? 'credit' : 'credits'}
          </Typography>
        </Alert>

        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Stack spacing={2}>
            {packages.map((pkg) => (
              <Card
                key={pkg.id}
                variant="outlined"
                sx={{
                  borderRadius: 3,
                  border: 2,
                  borderColor: pkg.id === 'pro' ? 'primary.main' : 'divider',
                  position: 'relative',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4,
                  },
                }}
              >
                {pkg.id === 'pro' && (
                  <Chip
                    label="Best Value"
                    color="primary"
                    size="small"
                    sx={{
                      position: 'absolute',
                      bottom: 12,
                      right: 12,
                      fontWeight: 600,
                    }}
                  />
                )}

                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" fontWeight={700} gutterBottom>
                        {pkg.name}
                      </Typography>

                      <Typography variant="h4" fontWeight={700} color="primary" gutterBottom>
                        ${pkg.priceUsd}
                        <Typography component="span" variant="body1" color="text.secondary">
                          {' '}USD
                        </Typography>
                      </Typography>

                      <Stack spacing={1} sx={{ mt: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CheckCircle fontSize="small" color="success" />
                          <Typography variant="body2">
                            <strong>{pkg.credits} mastering credits</strong>
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CheckCircle fontSize="small" color="success" />
                          <Typography variant="body2">
                            ${pkg.pricePerCredit.toFixed(2)} per credit
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CheckCircle fontSize="small" color="success" />
                          <Typography variant="body2">
                            Full-length mastered audio
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CheckCircle fontSize="small" color="success" />
                          <Typography variant="body2">
                            Unlimited tweaks per song
                          </Typography>
                        </Box>
                      </Stack>
                    </Box>

                    <Box sx={{ ml: 2 }}>
                      <Button
                        variant={pkg.id === 'pro' ? 'contained' : 'outlined'}
                        size="large"
                        onClick={() => handlePurchase(pkg.id)}
                        disabled={purchasing !== null}
                        sx={{
                          minWidth: 120,
                          borderRadius: 2,
                          fontWeight: 600,
                        }}
                      >
                        {purchasing === pkg.id ? (
                          <CircularProgress size={24} />
                        ) : (
                          'Purchase'
                        )}
                      </Button>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Stack>
        )}

        {/* Info Text */}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 3, textAlign: 'center' }}>
          Secure payment powered by Stripe • Credits never expire
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onClose} disabled={purchasing !== null}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PricingModal;
